
import { supabase } from "@/integrations/supabase/client";
import { Subscription, SubscriptionService, Customer } from "@/lib/data";

// Define an explicit interface for the minimal subscription data we return
interface MinimalSubscription extends Partial<Omit<Subscription, 'customer' | 'services'>> {
  subscription_id: string;
  subscription_number: string;
  status: "registered" | "active" | "cancelled" | "terminated";
  registered_at: string;
  customer?: Partial<Customer>;
  services: SubscriptionService[];
  installation_city?: string;
  installation_zip_code?: string;
  housing_type?: "apartment" | "house" | "office";
  next_bill_date?: string | null;
}

// Add a new interface to include MRC information
interface SubscriptionsWithMRC {
  subscriptions: MinimalSubscription[];
  totalMRC: number;
}

export async function getSubscriptionsMinimal(): Promise<SubscriptionsWithMRC> {
  try {
    // Get all subscriptions with minimal information needed
    const { data: subscriptionsData, error } = await supabase
      .from('subscriptions')
      .select(`
        subscription_id, 
        subscription_number, 
        status, 
        registered_at, 
        activation_date, 
        termination_date, 
        cancellation_date,
        installation_city,
        installation_zip_code,
        housing_type,
        next_bill_date,
        customer_id
      `);

    if (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }

    // If no subscriptions were found, return empty array with zero MRC
    if (!subscriptionsData || subscriptionsData.length === 0) {
      console.log('No subscriptions found');
      return { subscriptions: [], totalMRC: 0 };
    }

    console.log(`Retrieved ${subscriptionsData.length} subscription records`);

    // Get customer information for all subscriptions in a single query
    const { data: customerData, error: customerError } = await supabase
      .from('customers')
      .select('id, customer_name, customer_number')
      .in('id', subscriptionsData.map(s => s.customer_id));

    if (customerError) {
      console.error('Error fetching customer data:', customerError);
      // Continue with partial data instead of failing completely
    } else {
      console.log(`Retrieved customer data for ${customerData?.length || 0} customers`);
    }

    // Get services information for all subscriptions in a single query
    // This is the critical query for MRC calculations
    const { data: serviceData, error: serviceError } = await supabase
      .from('subscription_services')
      .select(`
        id, 
        subscription_id, 
        service_id,
        status, 
        price, 
        service:services(service_name, default_price)
      `)
      .in('subscription_id', subscriptionsData.map(s => s.subscription_id));

    if (serviceError) {
      console.error('Error fetching subscription services:', serviceError);
      // Continue with partial data instead of failing completely
    } else if (serviceData) {
      console.log(`Retrieved ${serviceData.length} subscription service records`);
    }

    // Create a map to efficiently organize services by subscription
    const servicesBySubscription: Record<string, SubscriptionService[]> = {};
    
    // Calculate total MRC from active services in active subscriptions
    let totalMRC = 0;
    
    if (serviceData && serviceData.length > 0) {
      // Get list of active subscription IDs for MRC calculation
      const activeSubscriptionIds = subscriptionsData
        .filter(sub => sub.status === 'active')
        .map(sub => sub.subscription_id);
      
      console.log(`Found ${activeSubscriptionIds.length} active subscriptions for MRC calculation`);
      
      serviceData.forEach(service => {
        // Process service for subscription organization
        if (!servicesBySubscription[service.subscription_id]) {
          servicesBySubscription[service.subscription_id] = [];
        }
        
        // Make sure we have valid price data
        let servicePrice = service.price;
        
        // If direct price is null, try to use default_price from service
        if (servicePrice === null && service.service?.default_price !== null && service.service?.default_price !== undefined) {
          servicePrice = service.service.default_price;
        }
        
        // Ensure price is a valid number
        if (typeof servicePrice !== 'number') {
          // Try to convert string to number if needed
          if (typeof servicePrice === 'string') {
            servicePrice = parseFloat(servicePrice);
            if (isNaN(servicePrice)) servicePrice = 0;
          } else {
            servicePrice = 0;
          }
        }

        // Calculate MRC: only count active services in active subscriptions
        if (service.status === 'active' && activeSubscriptionIds.includes(service.subscription_id)) {
          totalMRC += servicePrice;
        }
        
        servicesBySubscription[service.subscription_id].push({
          id: service.id,
          subscription_id: service.subscription_id,
          service_id: service.service_id,
          service: {
            service_id: service.service_id,
            service_name: service.service?.service_name || 'Unknown Service',
            default_price: service.service?.default_price || 0,
            description: '',
            parent_service_id: null
          },
          status: service.status as "registered" | "active" | "cancelled" | "terminated",
          registration_date: '',
          activation_date: null,
          termination_date: null,
          cancellation_date: null,
          price: servicePrice, // Use the validated price
          last_bill_date: null,
          next_bill_date: null
        });
      });
    }

    // Create a map to efficiently organize customers by their ID
    const customersById: Record<string, Partial<Customer>> = {};
    
    if (customerData && customerData.length > 0) {
      customerData.forEach(customer => {
        customersById[customer.id] = {
          id: customer.id,
          customer_name: customer.customer_name,
          customer_number: customer.customer_number
        };
      });
    }

    // Combine all data to create subscription objects
    const subscriptions: MinimalSubscription[] = subscriptionsData.map(subscription => {
      return {
        subscription_id: subscription.subscription_id,
        subscription_number: subscription.subscription_number,
        status: subscription.status as "registered" | "active" | "cancelled" | "terminated",
        registered_at: subscription.registered_at,
        activation_date: subscription.activation_date,
        termination_date: subscription.termination_date,
        cancellation_date: subscription.cancellation_date,
        installation_city: subscription.installation_city,
        installation_zip_code: subscription.installation_zip_code,
        housing_type: subscription.housing_type as "apartment" | "house" | "office" || "house",
        next_bill_date: subscription.next_bill_date,
        customer_id: subscription.customer_id,
        // Add customer information by looking up the customer ID
        customer: customersById[subscription.customer_id],
        // Add services information
        services: servicesBySubscription[subscription.subscription_id] || []
      };
    });

    console.log(`Successfully prepared ${subscriptions.length} subscriptions with minimal data`);
    
    return { 
      subscriptions: subscriptions,
      totalMRC: totalMRC 
    };
  } catch (error) {
    console.error('Unexpected error in getSubscriptionsMinimal:', error);
    // Return empty array and zero MRC instead of throwing to prevent UI from breaking
    return { subscriptions: [], totalMRC: 0 };
  }
}
